import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { GalleryLightbox } from '../components/GalleryLightbox';

export function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [subproductFilter, setSubproductFilter] = useState('');

  useEffect(() => {
    async function fetchProductData() {
      setLoading(true);
      try {
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/produkter?url_slug=${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            const item = data.items[0];
            setProduct(item);

            // Fetch related products in same category or child products
            const relatedRes = await fetch(
              `https://api.dev.auxo.dk/public/v2/content/produkter?limit=50`
            );
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              const filtered = (relatedData.items || []).filter(
                (rel) =>
                  rel.url_slug !== item.url_slug &&
                  rel.url_slug !== 'produkter' &&
                  (rel.category === item.category ||
                   rel.category === item.title ||
                   rel.parent_product_slug === item.url_slug ||
                   item.parent_product_slug === rel.url_slug)
              );
              setRelatedProducts(filtered);
            }
          } else {
            setProduct(null);
          }
        }
      } catch (err) {
        console.error('Error fetching product detail:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchProductData();
  }, [slug]);

  if (loading) {
    return (
      <div className="py-32 text-center text-neutral-500 text-sm bg-white min-h-[60vh]">
        <i className="fa-solid fa-spinner fa-spin mr-2 text-[#7FDCA7]" aria-hidden="true" />
        Henter produktoplysninger...
      </div>
    );
  }

  // Helper to optimize image URLs for high-resolution display
  const getHighResImage = (url, width = 1000) => {
    if (!url) return 'https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/placeholder.jpg';
    if (url.toLowerCase().endsWith('.bmp')) return url;
    if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  // Helper to clean duplicate leading header from CMS HTML description
  const cleanDescriptionHtml = (html) => {
    if (!html) return '';
    let cleaned = html.trim();
    // Strip leading <h1>, <h2> or <h3> tag if present at start of description HTML
    cleaned = cleaned.replace(/^<h[1-3][^>]*>[\s\S]*?<\/h[1-3]>\s*/i, '');

    // Convert dimension paragraph strings into formatted list if present as unformatted text
    cleaned = cleaned.replace(
      /<p>\s*Et flådeloft kan leveres i størrelsen:\s*([\d\s\x\–\-mm,]+)<\/p>/gi,
      (match, sizesStr) => {
        const sizes = sizesStr.split(',').map((s) => s.trim()).filter(Boolean);
        return `<p><strong>Et flådeloft kan leveres i følgende størrelser:</strong></p><ul class="list-disc pl-5 my-3 space-y-1">${sizes
          .map((size) => `<li>${size}</li>`)
          .join('')}</ul>`;
      }
    );

    return cleaned;
  };

  if (!product) {
    return (
      <div className="py-24 max-w-3xl mx-auto px-4 text-center space-y-6 bg-white min-h-[60vh]">
        <i className="fa-solid fa-triangle-exclamation text-4xl text-[#7FDCA7]" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-neutral-900 font-serif-heading">Produktet kunne ikke findes</h1>
        <p className="text-sm text-neutral-600">
          Det efterspurgte produkt findes desværre ikke på sitet.
        </p>
        <Link
          to="/produkter"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#7FDCA7] text-neutral-950 font-bold text-xs hover:bg-[#69C892] transition-colors rounded-xl"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til produktoversigt</span>
        </Link>
      </div>
    );
  }

  const images = product.images || [];

  const isSubproduct = Boolean(
    product.parent_product_slug ||
    (product.category && product.category.trim().toLowerCase() !== product.title.trim().toLowerCase())
  );

  return (
    <>
      <SEO
        title={`${product.title} | KELFAE LOFTER ApS`}
        description={
          product.summary ||
          `Specifikationer og oplysninger om ${product.title} fra KELFAE LOFTER ApS.`
        }
        ogImage={images[0]}
      />

      {/* Breadcrumb / Top Bar */}
      <div className="bg-neutral-100 border-b border-neutral-200 py-4 text-xs text-neutral-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-neutral-900 transition-colors">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-neutral-400" aria-hidden="true" />
          <Link to="/produkter" className="hover:text-neutral-900 transition-colors">
            Produkter
          </Link>
          {product.category && (
            <>
              <i className="fa-solid fa-chevron-right text-[10px] text-neutral-400" aria-hidden="true" />
              <span className="text-neutral-600">{product.category}</span>
            </>
          )}
          <i className="fa-solid fa-chevron-right text-[10px] text-neutral-400" aria-hidden="true" />
          <span className="text-neutral-900 font-bold">{product.title}</span>
        </div>
      </div>

      {/* Product Main Architectural Showcase Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          
          {/* EDITORIAL 2-ROW ALTERNATING LAYOUT */}
          <div className="space-y-12">
            
            {/* ROW 1: Image on Left, Title & Highlights on Right (img - tekst) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
              
              {/* LEFT: Image Showcase */}
              <div className="lg:col-span-5">
                {images.length > 0 ? (
                  <div className="space-y-3">
                    <div
                      onClick={() => {
                        setLightboxIndex(0);
                        setLightboxOpen(true);
                      }}
                      className="relative w-full h-[360px] sm:h-[420px] bg-neutral-100 border border-neutral-200 rounded-2xl overflow-hidden cursor-pointer group shadow-md hover:border-[#7FDCA7] transition-all duration-300"
                    >
                      <img
                        src={getHighResImage(images[0], 1200)}
                        alt={product.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors" />
                      <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-neutral-950/80 backdrop-blur-md text-white text-[11px] font-bold flex items-center gap-1.5 border border-white/20">
                        <i className="fa-solid fa-expand text-[10px]" aria-hidden="true" />
                        <span>Stor visning</span>
                      </div>
                    </div>

                    {/* Thumbnails if multiple */}
                    {images.length > 1 && (
                      <div className="grid grid-cols-4 gap-2.5">
                        {images.map((img, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              setLightboxIndex(idx);
                              setLightboxOpen(true);
                            }}
                            className={`relative rounded-xl overflow-hidden border cursor-pointer h-20 bg-neutral-100 transition-all ${
                              idx === lightboxIndex
                                ? 'border-[#7FDCA7] ring-2 ring-[#7FDCA7]/40'
                                : 'border-neutral-200 opacity-80 hover:opacity-100'
                            }`}
                          >
                            <img
                              src={getHighResImage(img, 400)}
                              alt={`${product.title} mini ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-12 text-center text-neutral-400">
                    <i className="fa-regular fa-image text-4xl mb-2" aria-hidden="true" />
                    <p className="text-xs">Intet produktbillede tilgængeligt</p>
                  </div>
                )}
              </div>

              {/* RIGHT: Title, Category Badge, Accent Line & Primary Action Buttons */}
              <div className="lg:col-span-7 flex flex-col space-y-6">
                <div>
                  {product.category && (
                    <span className="inline-block px-3.5 py-1.5 bg-[#7FDCA7]/20 border border-[#7FDCA7]/40 text-neutral-900 text-xs font-bold uppercase tracking-wider rounded-full mb-3">
                      {product.category}
                    </span>
                  )}
                  
                  {/* Main Heading H1 (Serif Display) */}
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 font-serif-heading leading-tight tracking-tight">
                    {product.title}
                  </h1>

                  {/* Emerald / Mint Accent Underline */}
                  <div className="mt-4 h-[3px] w-full bg-[#7FDCA7] rounded-full" />
                </div>

                {/* Short Summary if available */}
                {product.summary && (
                  <p className="text-sm sm:text-base text-neutral-700 leading-relaxed font-medium">
                    {product.summary}
                  </p>
                )}

                {/* Primary CTA Buttons Row */}
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <a
                    href={product?.external_url || "https://en.rockfon.international/products/rockfon-blanka-activity?redirected_parafon=true"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3.5 rounded-xl bg-[#7FDCA7] hover:bg-[#69C892] text-black font-bold text-xs uppercase tracking-wider transition-all shadow-sm flex items-center gap-2"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square text-sm text-black" aria-hidden="true" />
                    <span>Se produkt hos Rockfon</span>
                  </a>

                  <Link
                    to="/kontakt"
                    className="px-6 py-3.5 rounded-xl bg-[#7FDCA7] hover:bg-[#69C892] text-black font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2 shadow-sm"
                  >
                    <i className="fa-regular fa-paper-plane text-black" aria-hidden="true" />
                    <span>Indhent tilbud</span>
                  </Link>

                  <a
                    href="tel:43712540"
                    className="px-6 py-3.5 rounded-xl bg-white border border-neutral-300 text-black hover:bg-neutral-100 font-semibold text-xs transition-all flex items-center gap-2 shadow-xs"
                  >
                    <i className="fa-solid fa-phone text-[#2A8C52]" aria-hidden="true" />
                    <span>Ring: (+45) 43 71 25 40</span>
                  </a>
                </div>
              </div>

            </div>

            {/* ROW 2: Detailed Text on Left, PDF / Document Panel on Right (tekst - img) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start pt-10 border-t border-neutral-200">
              
              {/* LEFT: Full Editorial Description Text Block */}
              <div className="lg:col-span-7 flex flex-col space-y-6">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-900 border-b border-neutral-200 pb-3">
                  <i className="fa-solid fa-file-lines text-[#2A8C52]" aria-hidden="true" />
                  <span>Produktbeskrivelse & Specifikationer</span>
                </div>

                {product.description ? (
                  <div
                    className="prose prose-neutral max-w-none text-sm sm:text-base text-neutral-700 leading-relaxed space-y-5"
                    dangerouslySetInnerHTML={{ __html: cleanDescriptionHtml(product.description) }}
                  />
                ) : (
                  <p className="text-sm text-neutral-600 leading-relaxed italic">
                    Der er ingen yderligere tekstbeskrivelse for dette produkt. Kontakt os for rådgivning og specifikationer.
                  </p>
                )}
              </div>

              {/* RIGHT: PDF Downloads & Quick Action Box (img/panel side) */}
              <div className="lg:col-span-5 flex flex-col space-y-6">

                <div className="bg-neutral-50 border border-neutral-200 p-6 sm:p-7 rounded-2xl shadow-xs space-y-5">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-neutral-900 border-b border-neutral-200 pb-3">
                    <i className="fa-solid fa-sliders text-[#2A8C52]" aria-hidden="true" />
                    <span>Hurtigvalg & Dokumentation</span>
                  </div>

                  {/* PDF Downloads */}
                  {product.pdf_documents && product.pdf_documents.length > 0 ? (
                    <div className="space-y-2">
                      <span className="text-[11px] font-bold text-neutral-500 uppercase tracking-wider block">
                        Tekniske Datablade:
                      </span>
                      {product.pdf_documents.map((pdf, idx) => (
                        <a
                          key={idx}
                          href={pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-neutral-200 hover:border-[#7FDCA7] text-xs text-neutral-800 hover:text-neutral-900 transition-all group shadow-xs"
                        >
                          <div className="flex items-center gap-2.5">
                            <i className="fa-solid fa-file-pdf text-[#2A8C52] text-sm group-hover:scale-110 transition-transform" aria-hidden="true" />
                            <span className="font-semibold">
                              {product.pdf_labels || 'Download PDF Specifikationer'}
                            </span>
                          </div>
                          <i className="fa-solid fa-arrow-down text-neutral-400 group-hover:text-[#2A8C52] text-xs" aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-neutral-500 italic">
                      Kontakt os for at få tilsendt tekniske datablade og montageanvisninger for dette produkt.
                    </p>
                  )}

                  {/* Additional Contact Callout Box */}
                  <div className="p-4 rounded-xl bg-[#7FDCA7]/10 border border-[#7FDCA7]/30 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-bold text-neutral-900">
                      <i className="fa-solid fa-headset text-[#2A8C52]" aria-hidden="true" />
                      <span>Brug for teknisk rådgivning?</span>
                    </div>
                    <p className="text-xs text-neutral-600 leading-relaxed">
                      Vores konsulenter står klar til at rådgive om akustik, bæreevne og korrekt loftsystem til jeres projekt.
                    </p>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2A8C52] hover:underline pt-1"
                    >
                      <span>Kontakt KELFAE LOFTER</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* Sub-products / Related products Showcase */}
          {relatedProducts.length > 0 && (() => {
            const filteredRel = relatedProducts.filter((rel) =>
              rel.title?.toLowerCase().includes(subproductFilter.toLowerCase()) ||
              rel.summary?.toLowerCase().includes(subproductFilter.toLowerCase())
            );

            return (
              <div className="pt-16 border-t border-neutral-200 space-y-10">
                {/* Header & Subproduct Filter Bar */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-6 border-b border-neutral-200">
                  <div className="space-y-2 max-w-2xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7FDCA7]/15 border border-[#7FDCA7]/40 text-neutral-900 text-xs font-bold uppercase tracking-wider">
                      <i className="fa-solid fa-layer-group text-[#2A8C52]" aria-hidden="true" />
                      <span>{relatedProducts.length} Varianter i denne serie</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 font-serif-heading tracking-tight">
                      {product.is_category_page
                        ? `Produkter i ${product.title}`
                        : 'Relaterede produkter & varianter'}
                    </h2>
                    <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                      Udforsk de enkelte akustikplader, montagesystemer og specifikationer i denne produktserie.
                    </p>
                  </div>

                  {/* Search / Filter Input */}
                  <div className="flex items-center gap-3 w-full md:w-auto">
                    {relatedProducts.length > 4 && (
                      <div className="relative flex-1 md:w-72">
                        <i
                          className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 text-xs"
                          aria-hidden="true"
                        />
                        <input
                          type="text"
                          value={subproductFilter}
                          onChange={(e) => setSubproductFilter(e.target.value)}
                          placeholder="Søg i varianter..."
                          className="w-full pl-9 pr-8 py-2.5 bg-neutral-50 border border-neutral-300 rounded-xl text-xs text-neutral-900 focus:outline-none focus:border-[#7FDCA7] focus:ring-2 focus:ring-[#7FDCA7]/30 transition-all"
                        />
                        {subproductFilter && (
                          <button
                            onClick={() => setSubproductFilter('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700 text-xs"
                          >
                            <i className="fa-solid fa-xmark" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    )}

                    <Link
                      to="/produkter"
                      className="px-4 py-2.5 rounded-xl bg-[#7FDCA7] hover:bg-[#69C892] text-black font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2 whitespace-nowrap shadow-xs"
                    >
                      <span>Alle serier</span>
                      <i className="fa-solid fa-arrow-right text-[10px] text-black" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                {/* Subproducts Grid (Large Architectural Cards) */}
                {filteredRel.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredRel.map((rel, index) => {
                      const imgUrl = getHighResImage(rel.images?.[0], 1000);
                      const targetLink = rel.is_category_page
                        ? `/produkter/${rel.url_slug}`
                        : `/produkt/${rel.url_slug}`;
                      const hasPdf = rel.pdf_documents && rel.pdf_documents.length > 0;

                      return (
                        <div
                          key={rel.id || rel.url_slug || index}
                          className="group relative bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:border-[#7FDCA7] hover:shadow-2xl transition-all duration-500 flex flex-col justify-between"
                        >
                          {/* Top Highlight Accent Bar */}
                          <div className="h-1.5 w-full bg-neutral-200 group-hover:bg-[#7FDCA7] transition-colors duration-500" />

                          {/* Large Photo Showcase Container */}
                          <div className="relative h-72 sm:h-80 lg:h-88 bg-neutral-900 overflow-hidden">
                            <img
                              src={imgUrl}
                              alt={rel.title || 'Produktbillede'}
                              className="w-full h-full object-cover object-center group-hover:scale-108 transition-transform duration-700 ease-out"
                              loading="lazy"
                            />
                            {/* Gradient Mask for High Contrast */}
                            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/85 via-neutral-950/25 to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-500" />

                            {/* Floating Badges */}
                            <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-2 z-10">
                              {rel.category ? (
                                <span className="px-3 py-1 bg-neutral-950/85 text-[#7FDCA7] text-[11px] font-bold uppercase tracking-wider backdrop-blur-md rounded-lg border border-neutral-800 shadow-sm font-sans">
                                  {rel.category}
                                </span>
                              ) : <span />}

                              <div className="flex items-center gap-2">
                                {hasPdf && (
                                  <span className="px-2.5 py-1 bg-[#7FDCA7] text-neutral-950 text-[10px] font-extrabold uppercase tracking-wider backdrop-blur-md rounded-lg shadow-sm flex items-center gap-1">
                                    <i className="fa-solid fa-file-pdf text-[10px]" aria-hidden="true" />
                                    <span>PDF</span>
                                  </span>
                                )}
                                <span className="w-8 h-8 rounded-full bg-neutral-950/80 backdrop-blur-md text-white text-xs font-mono font-bold flex items-center justify-center border border-white/20 shadow-sm">
                                  {String(index + 1).padStart(2, '0')}
                                </span>
                              </div>
                            </div>

                            {/* Title overlay on bottom of image for impact */}
                            <div className="absolute bottom-4 left-6 right-6 z-10 space-y-1">
                              <h3 className="text-xl sm:text-2xl font-bold text-white font-serif-heading group-hover:text-[#7FDCA7] transition-colors duration-300 drop-shadow-md line-clamp-2">
                                {rel.title}
                              </h3>
                            </div>
                          </div>

                          {/* Card Content & Action Area */}
                          <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-5 bg-white">
                            <div>
                              {rel.summary ? (
                                <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed line-clamp-3">
                                  {rel.summary}
                                </p>
                              ) : (
                                <p className="text-xs text-neutral-400 italic">
                                  Se fulde specifikationer, mål og dokumentation for dette produkt.
                                </p>
                              )}
                            </div>

                            <div className="pt-4 border-t border-neutral-100 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 text-xs font-semibold text-neutral-500 group-hover:text-neutral-900 transition-colors">
                                <span className="w-2 h-2 rounded-full bg-[#7FDCA7] group-hover:scale-125 transition-transform" />
                                <span>Specifikationer & data</span>
                              </div>

                              <Link
                                to={targetLink}
                                className="px-5 py-2.5 rounded-xl bg-[#7FDCA7] hover:bg-[#69C892] text-black font-bold text-xs uppercase tracking-wider transition-all duration-300 flex items-center gap-2 shadow-xs group-hover:shadow-md shrink-0"
                                aria-label={`Læs mere om ${rel.title}`}
                              >
                                <span>Se produkt</span>
                                <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform text-black" aria-hidden="true" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-12 bg-neutral-50 rounded-2xl border border-neutral-200 text-center space-y-3">
                    <i className="fa-solid fa-magnifying-glass text-2xl text-neutral-400" aria-hidden="true" />
                    <p className="text-sm font-semibold text-neutral-700">
                      Ingen varianter matchede din søgning "{subproductFilter}"
                    </p>
                    <button
                      onClick={() => setSubproductFilter('')}
                      className="text-xs font-bold text-[#2A8C52] underline hover:text-[#1F2937]"
                    >
                      Nulstil søgning
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <GalleryLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
